/**
 * JUDAM layout constants — Phase 2 App Shell & Page Pattern
 * Mobile 16px / tablet 24px / PC 32px horizontal padding; max content 1280px.
 *
 * PWA (standalone): safe-area insets are non-zero on notched devices.
 * Browser tabs: usually 0. Always use env(..., 0px) fallback — never double-count
 * safe-area in both nav chrome and content unless intentional.
 */

export const PAGE_MAX_WIDTH = 1280;

/** MUI spacing: xs 16px, sm 24px, md+ 32px */
export const pagePaddingX = { xs: 2, sm: 3, md: 4 } as const;

/** Top padding inside page content area */
export const pagePaddingTop = { xs: 2, md: 3 } as const;

/**
 * Bottom padding inside page content area (24px).
 * Single source of truth — pages must not add their own bottom padding.
 */
export const pagePaddingBottom = 3;

export const MOBILE_HEADER_HEIGHT = '56px';
/** Visible tab bar height (icons + labels), excluding safe-area */
export const MOBILE_BOTTOM_NAV_HEIGHT = '75px';
/** Extra padding under tab icons (before home-indicator gap) */
export const MOBILE_BOTTOM_NAV_PADDING = '8px';

export const SAFE_AREA_TOP = 'env(safe-area-inset-top, 0px)';
export const SAFE_AREA_BOTTOM = 'env(safe-area-inset-bottom, 0px)';

/**
 * Full bottom chrome: tab bar + padding + home indicator.
 * Use for: fixed nav box height, main content paddingBottom, drawer calc.
 */
export const mobileBottomNavTotalHeight = `calc(${MOBILE_BOTTOM_NAV_HEIGHT} + ${MOBILE_BOTTOM_NAV_PADDING} + ${SAFE_AREA_BOTTOM})`;

export const mobileHeaderOffset =
  `calc(${MOBILE_HEADER_HEIGHT} + ${SAFE_AREA_TOP})`;

/** Main content clears bottom nav (includes safe-area once) */
export const mobileBottomOffset = mobileBottomNavTotalHeight;

export const desktopMainPaddingTop = '88px';
export const desktopMainPaddingBottom = '24px';

/** FAB sits above bottom nav + safe-area + gap */
export const mobileFabBottomOffset = `calc(${MOBILE_BOTTOM_NAV_HEIGHT} + ${MOBILE_BOTTOM_NAV_PADDING} + ${SAFE_AREA_BOTTOM} + 20px)`;

/**
 * Fixed action bars inside pages (Inventory save/in/out).
 * Same clearance as content — above bottom nav + safe-area.
 */
export const mobileFixedActionBottom = mobileBottomNavTotalHeight;

/** Stacking: content < FAB < bottom nav < modal */
export const Z_FAB = 1100;
export const Z_BOTTOM_NAV = 1200;
export const Z_MODAL = 1300;

/** Mobile drawer paper height (between header and bottom nav) */
export const mobileDrawerHeight = `calc(100dvh - ${MOBILE_HEADER_HEIGHT} - ${SAFE_AREA_TOP} - ${MOBILE_BOTTOM_NAV_HEIGHT} - ${MOBILE_BOTTOM_NAV_PADDING} - ${SAFE_AREA_BOTTOM} - 1px)`;

/** Desktop analytics grid — header(88px) + tabs(~48px) + bottom padding(24px) */
export const desktopAnalyticsGridHeight = `calc(100vh - ${desktopMainPaddingTop} - 48px - ${desktopMainPaddingBottom})`;

/** Shared app mobile breakpoint — match Layout shell (md = 960) */
export const APP_MOBILE_BREAKPOINT = 'md' as const;

/**
 * 요약 카드(총 매출·총 사용량 등) 공통 지오메트리 — 한 줄에 놓인 카드들이
 * 내용 길이와 무관하게 같은 높이·같은 정렬을 갖게 한다.
 * 패딩을 Card 쪽 중첩 선택자로 주는 이유: 테마의
 * `.MuiCard-root .MuiCardContent-root`(클래스 2개)가 CardContent의 sx(클래스 1개)를
 * 특이도로 눌러서, CardContent에 직접 준 padding/py는 무시되기 때문.
 */
export const summaryCardSx = {
  height: '100%',
  '& .MuiCardContent-root': {
    height: '100%',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
    textAlign: 'center',
    '&:last-child': { paddingBottom: '12px' },
  },
} as const;
